
import { useEffect, useMemo, useState } from "react";
import { Loader2, Search } from "lucide-react";

import { useLanguage } from "@/hooks/use-language";
import type { Category, Exam } from "@/lib/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isMobile;
}

const normalize = (s: string | undefined) => (s ?? "").toLowerCase();

export interface ExamPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  exams: Exam[];
  categoryMap?: Record<string, Category[]>;
  onPick: (examId: string) => void | Promise<void>;
  busyKey?: string | null;
  actionLabel: string;
  emptyLabel?: string;
}

export function ExamPicker({
  open,
  onOpenChange,
  title,
  description,
  exams,
  categoryMap,
  onPick,
  busyKey,
  actionLabel,
  emptyLabel,
}: ExamPickerProps) {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [query, setQuery] = useState("");

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setQuery("");
    onOpenChange(nextOpen);
  };

  const filtered = useMemo(() => {
    const q = normalize(query).trim();
    if (!q) return exams;
    return exams.filter((exam) => {
      if (normalize(exam.name).includes(q)) return true;
      if (normalize(exam.description).includes(q)) return true;
      const cats = categoryMap?.[exam.id] ?? [];
      return cats.some((c) => normalize(c.name).includes(q));
    });
  }, [exams, categoryMap, query]);

  const body = (
    <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-4">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("exam_picker.search_placeholder")}
          className="h-10 pl-9"
          autoFocus
        />
      </div>

      <div className="-mx-4 min-h-0 flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            {emptyLabel ?? t("exam_picker.no_results")}
          </div>
        ) : (
          <ul className="flex flex-col">
            {filtered.map((exam) => {
              const cats = categoryMap?.[exam.id] ?? [];
              const isBusy = busyKey === exam.id;
              return (
                <li key={exam.id}>
                  <button
                    type="button"
                    className={cn(
                      "group flex w-full cursor-pointer items-start gap-3 border-b border-border/60 px-4 py-3 text-left transition-colors",
                      "hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none",
                      "disabled:cursor-not-allowed disabled:opacity-60",
                    )}
                    disabled={isBusy}
                    onClick={() => void onPick(exam.id)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">
                        {exam.name}
                      </div>
                      {exam.description && (
                        <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                          {exam.description}
                        </div>
                      )}
                      {cats.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {cats.slice(0, 6).map((c) => (
                            <span
                              key={c.id}
                              className="rounded-full border border-border/70 px-2 py-0.5 text-[10px] text-muted-foreground"
                            >
                              {c.name}
                            </span>
                          ))}
                          {cats.length > 6 && (
                            <span className="self-center text-[10px] text-muted-foreground">
                              +{cats.length - 6}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <span
                      className={cn(
                        "inline-flex h-8 shrink-0 items-center justify-center rounded-lg border border-input bg-background px-3 text-xs font-medium transition-colors",
                        "group-hover:border-foreground/40 group-hover:bg-muted",
                      )}
                    >
                      {isBusy ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        actionLabel
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          className="flex max-h-[85vh] flex-col gap-3 rounded-t-2xl p-0 pt-4"
        >
          <SheetHeader className="px-4 pb-1">
            <SheetTitle>{title}</SheetTitle>
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[85svh] flex-col gap-3 overflow-hidden p-0 pt-4 sm:max-h-[80vh] sm:w-[min(560px,calc(100%-2rem))] sm:max-w-none">
        <DialogHeader className="px-4">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
